/**
 * Moment timeline: proportional segments colored by sentiment, a brand-safety stripe, a playhead,
 * a hover/focus hint and click or keyboard selection. Arrow keys move between moments, Home/End jump.
 */
import { useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { motionTokens } from '@/components/arc/lib/motion-tokens';
import type { MomentOut } from '@/api/types';
import { fmtDuration, labelize, truncate } from '@/lib/format';
import styles from './timeline.module.css';

export interface TimelineProps {
  moments: MomentOut[];
  /** Video length in seconds. Falls back to the end of the last moment. */
  duration: number;
  currentSec: number;
  selectedId: string | null;
  onSelect: (moment: MomentOut) => void;
  label?: string;
}

function tickStep(total: number): number {
  if (total > 3600) return 600;
  if (total > 900) return 120;
  if (total > 300) return 60;
  return 30;
}

export function Timeline({
  moments,
  duration,
  currentSec,
  selectedId,
  onSelect,
  label = 'Moment timeline',
}: TimelineProps) {
  const reduce = useReducedMotion();
  const trackRef = useRef<HTMLDivElement>(null);
  const [hint, setHint] = useState<MomentOut | null>(null);
  const hintId = useId();
  const total = Math.max(duration || moments.at(-1)?.end_sec || 0, 1);
  const selectedIndex = moments.findIndex((m) => m.id === selectedId);
  const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const ticks = useMemo(() => {
    const step = tickStep(total);
    const out: number[] = [];
    for (let t = 0; t <= total; t += step) out.push(t);
    return out;
  }, [total]);

  const focusIndex = (i: number) => {
    const target = moments[i];
    if (!target) return;
    onSelect(target);
    trackRef.current?.querySelectorAll<HTMLButtonElement>('[data-segment]')[i]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (moments.length === 0) return;
    const current = selectedIndex >= 0 ? selectedIndex : 0;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        focusIndex(Math.min(moments.length - 1, current + 1));
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        focusIndex(Math.max(0, current - 1));
        break;
      case 'Home':
        e.preventDefault();
        focusIndex(0);
        break;
      case 'End':
        e.preventDefault();
        focusIndex(moments.length - 1);
        break;
      default:
    }
  };

  const pct = (sec: number) => `${Math.min(100, Math.max(0, (sec / total) * 100))}%`;
  const selected = selectedIndex >= 0 ? moments[selectedIndex] : undefined;
  const hintTransition = reduce
    ? { duration: 0 }
    : {
        duration: motionTokens.duration.fast,
        ease: [...motionTokens.ease.enter] as [number, number, number, number],
      };

  return (
    <div className={styles.root}>
      <div
        ref={trackRef}
        className={styles.track}
        role="group"
        aria-label={label}
        aria-describedby={hint ? hintId : undefined}
        onKeyDown={onKeyDown}
      >
        {moments.map((m, i) => {
          const risk = m.brand_safety?.risk;
          const width = Math.max(((m.end_sec - m.start_sec) / total) * 100, 0.15);
          const isSelected = m.id === selectedId;
          const name = [
            `Moment ${i + 1} of ${moments.length}`,
            `${fmtDuration(m.start_sec)} to ${fmtDuration(m.end_sec)}`,
            `${labelize(m.sentiment)} sentiment`,
            risk && risk !== 'low' ? `${labelize(risk)} brand-safety risk` : null,
            m.brands.length ? `${m.brands.length} brand${m.brands.length === 1 ? '' : 's'}` : null,
          ]
            .filter(Boolean)
            .join(', ');
          return (
            <button
              key={m.id}
              type="button"
              data-segment=""
              data-sentiment={m.sentiment}
              data-risk={risk ?? undefined}
              data-selected={isSelected || undefined}
              tabIndex={i === tabbableIndex ? 0 : -1}
              aria-label={name}
              aria-pressed={isSelected}
              className={styles.segment}
              style={{ left: pct(m.start_sec), width: `${width}%` }}
              onClick={() => onSelect(m)}
              onMouseEnter={() => setHint(m)}
              onMouseLeave={() => setHint(null)}
              onFocus={() => setHint(m)}
              onBlur={() => setHint(null)}
            >
              {m.brands.length > 0 && <span className={styles.brandDot} aria-hidden="true" />}
            </button>
          );
        })}
        {selected && (
          <span
            className={styles.selection}
            aria-hidden="true"
            style={{
              left: pct(selected.start_sec),
              width: `${Math.max(((selected.end_sec - selected.start_sec) / total) * 100, 0.15)}%`,
            }}
          />
        )}
        <span className={styles.playhead} aria-hidden="true" style={{ left: pct(currentSec) }} />
        <AnimatePresence>
          {hint && (
            <motion.div
              key={hint.id}
              id={hintId}
              role="tooltip"
              className={styles.hint}
              style={{ '--x': pct((hint.start_sec + hint.end_sec) / 2) } as CSSProperties}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: reduce ? 0 : motionTokens.duration.instant } }}
              transition={hintTransition}
            >
              <span className={`${styles.hintTime} mono`}>
                {fmtDuration(hint.start_sec)} to {fmtDuration(hint.end_sec)}
              </span>
              <span className={styles.hintText}>{truncate(hint.scene_summary, 140)}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className={styles.ticks} aria-hidden="true">
        {ticks.map((t) => (
          <span key={t} className={`${styles.tick} mono`} style={{ left: pct(t) }}>
            {fmtDuration(t)}
          </span>
        ))}
      </div>
      <ul className={styles.legend} aria-label="Legend">
        <li>
          <span className={styles.swatch} data-sentiment="positive" aria-hidden="true" /> Positive
        </li>
        <li>
          <span className={styles.swatch} data-sentiment="neutral" aria-hidden="true" /> Neutral
        </li>
        <li>
          <span className={styles.swatch} data-sentiment="negative" aria-hidden="true" /> Negative
        </li>
        <li>
          <span className={styles.swatch} data-risk="high" aria-hidden="true" /> Risk stripe (high or medium)
        </li>
        <li>
          <span className={styles.swatchDot} aria-hidden="true" /> Brand present
        </li>
      </ul>
    </div>
  );
}
